import {
  type PersonReport,
  PersonReportSchema,
  type ProjectReport,
  ProjectReportSchema,
  type Rollup,
  RollupSchema,
  type Running,
  RunningSchema,
  type TeamReport,
  TeamReportSchema,
  type TimeEntry,
  TimeEntrySchema,
} from '../model/schema'

export interface TimeEntryDto {
  id: string
  task_id: string
  employee_id: string
  started_at: string
  stopped_at?: string
  source: string
}

export interface RollupDto {
  task_id: string
  total_seconds: number
  own_seconds: number
  running: boolean
}

export interface RunningDto {
  entry: TimeEntryDto
  task: { id: string; title: string; board_id: string; column_id: string }
}

export function toTimeEntry(dto: TimeEntryDto): TimeEntry {
  return TimeEntrySchema.parse({
    id: dto.id,
    taskId: dto.task_id,
    employeeId: dto.employee_id,
    startedAt: dto.started_at,
    stoppedAt: dto.stopped_at ?? null,
    source: dto.source,
  })
}

export function toRollup(dto: RollupDto): Rollup {
  return RollupSchema.parse({
    taskId: dto.task_id,
    totalSeconds: dto.total_seconds,
    ownSeconds: dto.own_seconds,
    running: dto.running,
  })
}

export function toRunning(dto: RunningDto): Running {
  return RunningSchema.parse({
    entryId: dto.entry.id,
    startedAt: dto.entry.started_at,
    taskId: dto.task.id,
    taskTitle: dto.task.title,
    boardId: dto.task.board_id,
    columnId: dto.task.column_id,
  })
}

export interface EmployeeGroupDto {
  id: string
  name: string
  department?: string
  seconds: number
}

export interface ProjectGroupDto {
  id: string
  name: string
  seconds: number
}

export interface ProjectReportDto {
  project_id: string
  project_name: string
  total_seconds: number
  by_employee: EmployeeGroupDto[]
}

export interface PersonReportDto {
  employee_id: string
  employee_name: string
  total_seconds: number
  by_project: ProjectGroupDto[]
}

// SQL отчёта на бэке без ORDER BY — порядок групп фиксируем на клиенте:
// старшая доля сверху.
function bySecondsDesc(a: { seconds: number }, b: { seconds: number }): number {
  return b.seconds - a.seconds
}

export function toProjectReport(dto: ProjectReportDto): ProjectReport {
  return ProjectReportSchema.parse({
    projectId: dto.project_id,
    projectName: dto.project_name,
    totalSeconds: dto.total_seconds,
    byEmployee: (dto.by_employee ?? [])
      .map((g) => ({
        id: g.id,
        name: g.name,
        department: g.department ?? null,
        seconds: g.seconds,
      }))
      .sort(bySecondsDesc),
  })
}

export interface TeamMemberDto {
  id: string
  name: string
  department?: string
  seconds: number
  task_count: number
  focus_seconds: number
}

export interface TeamReportDto {
  total_seconds: number
  total_focus_seconds: number
  members: TeamMemberDto[]
}

// Порядок строк команды считает бэк (время убыв., при равенстве по имени), и
// клиент его не трогает: пересортировка развела бы «кто первый» на экране и в
// любом другом потребителе того же ответа.
export function toTeamReport(dto: TeamReportDto): TeamReport {
  return TeamReportSchema.parse({
    totalSeconds: dto.total_seconds,
    totalFocusSeconds: dto.total_focus_seconds,
    members: (dto.members ?? []).map((m) => ({
      id: m.id,
      name: m.name,
      department: m.department ?? null,
      seconds: m.seconds,
      taskCount: m.task_count,
      focusSeconds: m.focus_seconds,
    })),
  })
}

export function toPersonReport(dto: PersonReportDto): PersonReport {
  return PersonReportSchema.parse({
    employeeId: dto.employee_id,
    employeeName: dto.employee_name,
    totalSeconds: dto.total_seconds,
    byProject: (dto.by_project ?? [])
      .map((g) => ({
        id: g.id,
        name: g.name,
        seconds: g.seconds,
      }))
      .sort(bySecondsDesc),
  })
}
